import React, { useState, useEffect, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import Modal from '../../../../components/Modal';
import {
  MatrixSection,
  SkillCategory,
  SKILL_CATEGORIES,
} from '../../config';

interface SectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** When set, the modal edits this section; otherwise it creates a new one. */
  section: MatrixSection | null;
  /** All sections (used to append new ones at the end of their category). */
  sections: MatrixSection[];
}

const SectionModal: React.FC<SectionModalProps> = ({ isOpen, onClose, onSaved, section, sections }) => {
  const supabase = useSupabaseClient();
  const isEdit = section !== null;

  const [category, setCategory] = useState<SkillCategory>('Design');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCategory(section?.category ?? 'Design');
      setName(section?.name ?? '');
      setError(null);
      setConfirmRemove(false);
    }
  }, [isOpen, section]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Section name is required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isEdit) {
        const { error: updateError } = await supabase
          .from('skill_sections')
          .update({ name: trimmed })
          .eq('id', section.id);
        if (updateError) {
          // The colliding row may be a soft-deleted section that isn't
          // visible in the matrix — explain rather than show a raw error.
          if (updateError.code === '23505') {
            setError('A section with this name already exists in this category — it may be a previously removed section. Use “+ Add Section” with this name to restore it instead.');
            return;
          }
          throw updateError;
        }
      } else {
        const sortOrder = Math.max(
          -1,
          ...sections.filter(s => s.category === category).map(s => s.sort_order)
        ) + 1;
        const { error: insertError } = await supabase
          .from('skill_sections')
          .insert({ category, name: trimmed, sort_order: sortOrder });

        if (insertError) {
          // Unique (category, name) violation: an identical section exists. If it
          // was soft-deleted, reactivate it (restores its skills' history) rather
          // than failing.
          if (insertError.code === '23505') {
            const { data: existing, error: lookupError } = await supabase
              .from('skill_sections')
              .select('id, is_active')
              .eq('category', category)
              .eq('name', trimmed)
              .single();
            if (lookupError) throw lookupError;
            if (existing.is_active) {
              setError('A section with this name already exists in this category.');
              return;
            }
            const { error: reactivateError } = await supabase
              .from('skill_sections')
              .update({ is_active: true })
              .eq('id', existing.id);
            if (reactivateError) throw reactivateError;
          } else {
            throw insertError;
          }
        }
      }

      onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving section:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while saving the section.');
    } finally {
      setIsSubmitting(false);
    }
  }, [name, category, isEdit, section, sections, supabase, onSaved, onClose]);

  const handleRemove = useCallback(async () => {
    if (!section) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const { error: removeError } = await supabase
        .from('skill_sections')
        .update({ is_active: false })
        .eq('id', section.id);
      if (removeError) throw removeError;
      onSaved();
      onClose();
    } catch (err) {
      console.error('Error removing section:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while removing the section.');
    } finally {
      setIsSubmitting(false);
    }
  }, [section, supabase, onSaved, onClose]);

  const modalFooter = confirmRemove ? (
    <div className="delete-confirm">
      <span>Remove this section and hide its skills from the matrix?</span>
      <button type="button" className="btn-secondary" onClick={() => setConfirmRemove(false)} disabled={isSubmitting}>
        Cancel
      </button>
      <button type="button" className="btn-danger" onClick={handleRemove} disabled={isSubmitting}>
        {isSubmitting ? 'Removing...' : 'Remove Section'}
      </button>
    </div>
  ) : (
    <div className="modal-footer-actions">
      {isEdit && (
        <button type="button" className="btn-danger" onClick={() => setConfirmRemove(true)} disabled={isSubmitting}>
          Remove
        </button>
      )}
      <button type="button" onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button type="submit" form="section-form" disabled={isSubmitting} className="btn-primary">
        {isSubmitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Section'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Section' : 'New Section'}
      size="sm"
      className="skills-modal"
      hasFooter={true}
      footerContent={modalFooter}
    >
      <div className="form-content">
        <form id="section-form" onSubmit={handleSubmit}>
          {error && <div className="error-message general-error">{error}</div>}
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="section-category">Category</label>
              <div className="skills-category-selector">
                {SKILL_CATEGORIES.map(cat => (
                  <label key={cat} className="radio-option">
                    <input
                      type="radio"
                      name="sectionCategory"
                      value={cat}
                      checked={category === cat}
                      onChange={() => setCategory(cat)}
                      disabled={isEdit}
                    />
                    <span>{cat}</span>
                  </label>
                ))}
              </div>
              {isEdit && (
                <div className="field-hint">Category can’t be changed after a section is created.</div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="section-name">Section Name</label>
              <input
                id="section-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Industrial"
                autoFocus
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default SectionModal;
